import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  getCurrentUser,
  getToken,
  isLocalAuthentication,
  logout as cognitoLogout,
} from '../services/auth';
import { getMe } from '../services/api';

const AuthContext = createContext(null);

/**
 * Return the user with its live role, dashboard tabs and manageable roles
 * from GET /api/v1/me. If /me fails, keep the user signed in but grant no
 * dashboard access; the backend gates every admin call anyway.
 */
export async function withPermissions(user) {
  try {
    const me = await getMe();
    return { ...user, role: me.role, tabs: me.tabs, manageableRoles: me.manageable_roles };
  } catch (error) {
    console.error('Failed to load permissions', error);
    return { ...user, tabs: [], manageableRoles: [] };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  useEffect(() => {
    getCurrentUser().then(async (u) => {
      // Rotation credentials live only in memory. If a restricted page is
      // refreshed, clear its tokens and require the current password again.
      if (u?.passwordChangeRequired) {
        cognitoLogout();
      } else if (u) {
        setUser(await withPermissions(u));
      }
      setIsAuthChecked(true);
    });
  }, []);

  const login = (role, username) => {
    // Sign in at once; dashboard access follows when /me answers.
    setUser({ role, username, tabs: [], manageableRoles: [] });
    withPermissions({ role, username }).then((loaded) => {
      setUser(current => (current?.username === username ? loaded : current));
    });
  };

  const logout = useCallback(() => {
    // Clear current and orphaned chat data from this tab session.
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (
        key === 'chat_session_id' ||
        key?.startsWith('chat_messages_') ||
        key?.startsWith('chat_rooms_') ||
        key?.startsWith('chat_current_')
      ) {
        sessionStorage.removeItem(key);
      }
    }

    cognitoLogout();
    setUser(null);
  }, []);

  // Periodically check if Cognito session is still valid (every 60s)
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    if (!user || isLocalAuthentication()) return;
    const interval = setInterval(async () => {
      const token = await getToken();
      if (!token) {
        logoutRef.current();
      }
    }, 60_000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, isAuthChecked, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
